import { useEffect, useRef } from 'react'
import { TerraDraw, TerraDrawCircleMode, TerraDrawPolygonMode, TerraDrawRectangleMode } from 'terra-draw'
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter'

export type DrawnShapeType = 'circle' | 'rectangle' | 'polygon'

type ShapeStyleOptions = google.maps.CircleOptions | google.maps.RectangleOptions | google.maps.PolygonOptions

interface UseMapShapeDrawingParams {
  map: google.maps.Map | null
  drawingMode: string
  circleOptions: google.maps.CircleOptions
  rectangleOptions: google.maps.RectangleOptions
  polygonOptions: google.maps.PolygonOptions
  onShapeComplete: (type: DrawnShapeType, shape: google.maps.Circle | google.maps.Rectangle | google.maps.Polygon) => void
}

const toTerraDrawStyles = (options: ShapeStyleOptions) => ({
  fillColor: (options.fillColor ?? '#313FFE') as `#${string}`,
  fillOpacity: options.fillOpacity ?? 0.15,
  outlineColor: (options.strokeColor ?? '#cc6600') as `#${string}`,
  outlineWidth: options.strokeWeight ?? 2
})

const terraDrawModeFor = (drawingMode: string) => (drawingMode === 'circle' || drawingMode === 'rectangle' || drawingMode === 'polygon' ? drawingMode : 'static')

const outerRing = (feature: { geometry: { coordinates: unknown } }) => (feature.geometry.coordinates as number[][][])[0]

const ringCenter = (ring: number[][]) => {
  const corners = ring.slice(0, -1)
  const summed = corners.reduce((accumulated, [lng, lat]) => ({ lat: accumulated.lat + lat, lng: accumulated.lng + lng }), { lat: 0, lng: 0 })
  return { lat: summed.lat / corners.length, lng: summed.lng / corners.length }
}

const featureToGoogleShape = (feature: { properties: Record<string, unknown>; geometry: { coordinates: unknown } }): { type: DrawnShapeType; shape: google.maps.Circle | google.maps.Rectangle | google.maps.Polygon } | null => {
  const ring = outerRing(feature)
  if (!ring || ring.length < 4) {
    return null
  }

  const mode = feature.properties.mode

  if (mode === 'circle') {
    const center = ringCenter(ring)
    const radius = Number(feature.properties.radiusKilometers) * 1000
    return { type: 'circle', shape: new google.maps.Circle({ center, radius }) }
  }

  if (mode === 'rectangle' || mode === 'angled-rectangle') {
    const bounds = new google.maps.LatLngBounds()
    ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
    return { type: 'rectangle', shape: new google.maps.Rectangle({ bounds }) }
  }

  if (mode === 'polygon') {
    const path = ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
    return { type: 'polygon', shape: new google.maps.Polygon({ paths: path }) }
  }

  return null
}

export const useMapShapeDrawing = ({ map, drawingMode, circleOptions, rectangleOptions, polygonOptions, onShapeComplete }: UseMapShapeDrawingParams) => {
  const onShapeCompleteRef = useRef(onShapeComplete)
  onShapeCompleteRef.current = onShapeComplete
  const drawingModeRef = useRef(drawingMode)
  drawingModeRef.current = drawingMode
  const drawRef = useRef<TerraDraw | null>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    if (!map) {
      return
    }

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ map, lib: google.maps }),
      modes: [new TerraDrawCircleMode({ styles: toTerraDrawStyles(circleOptions) }), new TerraDrawRectangleMode({ styles: toTerraDrawStyles(rectangleOptions) }), new TerraDrawPolygonMode({ styles: toTerraDrawStyles(polygonOptions) })]
    })

    draw.on('finish', (id, context) => {
      if (context.action !== 'draw') {
        return
      }
      const finishedFeature = draw.getSnapshot().find(feature => feature.id === id)
      draw.removeFeatures([id])
      if (!finishedFeature) {
        return
      }
      const converted = featureToGoogleShape(finishedFeature)
      if (converted) {
        onShapeCompleteRef.current(converted.type, converted.shape)
      }
    })
    drawRef.current = draw

    const startDrawing = () => {
      if (startedRef.current) {
        return
      }
      draw.start()
      draw.setMode(terraDrawModeFor(drawingModeRef.current))
      startedRef.current = true
    }

    const mapHasRendered = Boolean(map.getDiv().querySelector('.gm-style'))
    const idleListener = mapHasRendered ? undefined : google.maps.event.addListenerOnce(map, 'idle', startDrawing)
    if (mapHasRendered) {
      startDrawing()
    }

    return () => {
      if (idleListener) {
        google.maps.event.removeListener(idleListener)
      }
      if (startedRef.current) {
        draw.stop()
      }
      startedRef.current = false
      drawRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  useEffect(() => {
    if (drawRef.current && startedRef.current) {
      drawRef.current.setMode(terraDrawModeFor(drawingMode))
    }
  }, [drawingMode])
}
